import React, { useState, useEffect } from 'react';
import { Package, TrendingUp, TrendingDown, Calendar, DollarSign, RefreshCw, ChevronDown, ChevronUp, ArrowRight, Repeat, Plus, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const InvestmentLotsSection = ({ portfolioId, portfolioName, clientId, isAdminPreview = false }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [expandedLots, setExpandedLots] = useState({});
  const [lotTransactions, setLotTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState({});

  const fetchInvestmentLots = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = isAdminPreview && clientId
        ? `${API_URL}/api/admin/view-as/${clientId}/portfolio/${portfolioId}/investment-lots`
        : `${API_URL}/api/client/portfolio/${portfolioId}/investment-lots`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investment lots');
      }

      const data = await response.json();
      console.log('📊 Investment lots received from API:', data.length, 'lots');
      if (data.length > 0) {
        console.log('📊 First lot sample:', data[0]);
        console.log('📊 Current values check:', {
          current_asset_units: data[0].current_asset_units,
          current_asset_value: data[0].current_asset_value,
          current_asset_price: data[0].current_asset_price
        });
      }
      setLots(data);
    } catch (err) {
      console.error('Error fetching investment lots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (portfolioId) {
      fetchInvestmentLots();
    }
  }, [portfolioId, isAdminPreview, clientId]);

  const fetchLotTransactions = async (lotId) => {
    if (lotTransactions[lotId]) {
      // Already fetched, just toggle expand
      return;
    }

    setLoadingTransactions(prev => ({ ...prev, [lotId]: true }));
    try {
      const url = isAdminPreview && clientId
        ? `${API_URL}/api/admin/view-as/${clientId}/lot/${lotId}/transactions`
        : `${API_URL}/api/client/lot/${lotId}/transactions`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch lot transactions');
      }

      const data = await response.json();
      setLotTransactions(prev => ({ ...prev, [lotId]: data }));
    } catch (err) {
      console.error('Error fetching lot transactions:', err);
    } finally {
      setLoadingTransactions(prev => ({ ...prev, [lotId]: false }));
    }
  };

  const toggleLotExpand = async (lotId) => {
    const isExpanded = expandedLots[lotId];
    
    if (!isExpanded) {
      // Expanding - fetch transactions if not already fetched
      await fetchLotTransactions(lotId);
    }
    
    setExpandedLots(prev => ({ ...prev, [lotId]: !isExpanded }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount, decimals = 2) => {
    if (amount == null) return '—';
    const numValue = Number(amount);
    if (isNaN(numValue)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue);
  };

  const formatNumber = (num, decimals = 2) => {
    if (num == null) return '—';
    const numValue = Number(num);
    if (isNaN(numValue)) return '—';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue);
  };

  const formatPercentage = (value, decimals = 2) => {
    if (value == null) return '—';
    const numValue = Number(value);
    if (isNaN(numValue)) return '—';
    const percentage = numValue * 100;
    return `${percentage >= 0 ? '+' : ''}${formatNumber(percentage, decimals)}%`;
  };

  const formatDays = (days) => {
    if (days == null || isNaN(days)) return '—';
    return `${days}d`;
  };

  const getMovementTypeInfo = (movementType) => {
    const type = (movementType || '').toUpperCase();
    switch (type) {
      case 'INITIAL':
        return { icon: Plus, label: 'Initial Deposit', color: 'text-emerald-400' };
      case 'DEPOSIT':
        return { icon: Plus, label: 'Deposit', color: 'text-emerald-400' };
      case 'TRANSFER':
        return { icon: Repeat, label: 'Transfer', color: 'text-blue-400' };
      case 'WITHDRAWAL':
        return { icon: Minus, label: 'Withdrawal', color: 'text-red-400' };
      case 'BUY':
        return { icon: TrendingUp, label: 'Buy', color: 'text-purple-400' };
      case 'SELL':
        return { icon: TrendingDown, label: 'Sell', color: 'text-orange-400' };
      default:
        return { icon: ArrowRight, label: type || 'Movement', color: 'text-[#808080]' };
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedLots = React.useMemo(() => {
    const sorted = [...lots];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle dates
      if (sortConfig.key === 'date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [lots, sortConfig]);

  const summary = React.useMemo(() => {
    if (!lots.length) return null;
    
    const totalPurchaseValue = lots.reduce((sum, lot) => sum + (parseFloat(lot.purchase_value) || 0), 0);
    const totalCurrentValue = lots.reduce((sum, lot) => sum + (parseFloat(lot.current_asset_value) || 0), 0);
    const totalGains = lots.reduce((sum, lot) => sum + (parseFloat(lot.dollar_gains) || 0), 0);
    
    // Calculate weighted average CAGR (weighted by purchase value)
    const weightedCAGRSum = lots.reduce((sum, lot) => {
      const purchaseValue = parseFloat(lot.purchase_value) || 0;
      const cagr = parseFloat(lot.cagr) || 0;
      return sum + (cagr * purchaseValue);
    }, 0);
    const avgCAGR = totalPurchaseValue > 0 ? weightedCAGRSum / totalPurchaseValue : 0;
    
    return {
      totalLots: lots.length,
      totalPurchaseValue,
      totalCurrentValue,
      totalGains,
      avgCAGR,
    };
  }, [lots]);

  if (loading) {
    return (
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-[#676DFF] animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6">
        <div className="text-center py-8">
          <p className="text-red-400 text-sm">Error loading investment lots: {error}</p>
          <button
            onClick={fetchInvestmentLots}
            className="mt-4 px-4 py-2 text-sm bg-[#676DFF] hover:bg-[#5A60E8] text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!lots.length) {
    return (
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-[#676DFF]" />
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Lexend, sans-serif' }}>
              Investment Lots
            </h3>
          </div>
        </div>
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-[#606060] mx-auto mb-4" />
          <p className="text-[#808080] text-sm">No investment lots found for this portfolio</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#151515] border border-white/10 rounded-xl overflow-hidden" style={{ fontFamily: 'Lexend, sans-serif' }}>
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-[#676DFF]" />
            <h3 className="text-lg font-semibold text-white">Investment Lots</h3>
          </div>
          <button
            onClick={fetchInvestmentLots}
            className="p-2 rounded-lg border border-white/20 hover:bg-white/5 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4 text-[#B8B8B8]" />
          </button>
        </div>
        <p className="text-sm text-[#808080]">
          Showing {lots.length} lot{lots.length !== 1 ? 's' : ''} for {portfolioName}
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 border-b border-white/10 bg-white/[0.02]">
          <div className="space-y-1">
            <p className="text-xs text-[#808080] uppercase tracking-wider">Total Lots</p>
            <p className="text-xl font-semibold text-white">{summary.totalLots}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#808080] uppercase tracking-wider">Total P&L</p>
            <p className={`text-xl font-semibold ${summary.totalGains >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {summary.totalGains >= 0 ? '+' : ''}{formatCurrency(summary.totalGains, 0)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#808080] uppercase tracking-wider">Avg CAGR</p>
            <p className={`text-xl font-semibold ${summary.avgCAGR >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatPercentage(summary.avgCAGR)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#808080] uppercase tracking-wider">Current Value</p>
            <p className="text-xl font-semibold text-white">{formatCurrency(summary.totalCurrentValue, 0)}</p>
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-[#808080] text-xs uppercase tracking-wider">
              <th 
                className="px-4 py-3 text-left font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Date
                </div>
              </th>
              <th className="px-4 py-3 text-right font-semibold">Purchase Info</th>
              <th className="px-4 py-3 text-right font-semibold">Current Info</th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('dollar_gains')}
              >
                <div className="flex items-center justify-end gap-1">
                  <DollarSign className="h-3 w-3" />
                  Gains
                </div>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('cagr')}
              >
                CAGR
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedLots.map((lot) => {
              const gains = parseFloat(lot.dollar_gains) || 0;
              const cagr = parseFloat(lot.cagr) || 0;
              const absReturns = parseFloat(lot.absolute_returns) || 0;
              const isExpanded = expandedLots[lot.lot_id];
              const transactions = lotTransactions[lot.lot_id] || [];
              const isLoadingTxn = loadingTransactions[lot.lot_id];

              return (
                <React.Fragment key={lot.lot_id}>
                  <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleLotExpand(lot.lot_id)}
                          className="p-1 rounded hover:bg-white/10 transition-colors"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-[#676DFF]" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-[#676DFF]" />
                          )}
                        </button>
                        <div>
                          <div className="text-sm text-white">{formatDate(lot.date)}</div>
                          <div className="text-xs text-[#808080]">{formatDays(lot.holding_days)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="text-sm text-white">
                        {formatNumber(lot.purchased_asset_units, 4)} {lot.purchased_asset_denomination}
                      </div>
                      <div className="text-xs text-[#808080]">
                        @ {formatCurrency(lot.purchase_price, 2)}
                      </div>
                      <div className="text-xs text-[#B8B8B8] font-semibold mt-0.5">
                        {formatCurrency(lot.purchase_value)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="text-sm text-white">
                        {formatNumber(lot.current_asset_units, 4)} {lot.purchased_asset_denomination}
                      </div>
                      <div className="text-xs text-[#808080]">
                        @ {formatCurrency(lot.current_asset_price, 2)}
                      </div>
                      <div className="text-xs text-[#B8B8B8] font-semibold mt-0.5">
                        {formatCurrency(lot.current_asset_value)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className={`text-sm font-semibold ${gains >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {gains >= 0 ? '+' : ''}{formatCurrency(gains)}
                      </div>
                      <div className={`text-xs ${absReturns >= 0 ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                        {formatPercentage(absReturns)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {cagr >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-400" />
                        )}
                        <span className={`text-lg font-bold ${cagr >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatPercentage(cagr)}
                        </span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded transaction details */}
                  {isExpanded && (
                    <tr className="bg-white/[0.02]">
                      <td colSpan="5" className="px-4 py-4">
                        <div className="pl-10">
                          <div className="flex items-center gap-2 mb-3">
                            <Package className="h-4 w-4 text-[#676DFF]" />
                            <h4 className="text-sm font-semibold text-white">Transaction History</h4>
                          </div>
                          
                          {isLoadingTxn ? (
                            <div className="flex items-center justify-center py-4">
                              <RefreshCw className="h-5 w-5 text-[#676DFF] animate-spin" />
                            </div>
                          ) : transactions.length === 0 ? (
                            <p className="text-xs text-[#808080] py-2">No transactions found</p>
                          ) : (
                            <div className="space-y-2">
                              {transactions.map((txn, idx) => {
                                const typeInfo = getMovementTypeInfo(txn.movement_type);
                                const TypeIcon = typeInfo.icon;
                                
                                return (
                                  <div 
                                    key={txn.movement_id || idx} 
                                    className="p-3 rounded-lg bg-[#151515] border border-white/5 space-y-2"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <TypeIcon className={`h-4 w-4 ${typeInfo.color}`} />
                                        <div>
                                          <div className="text-sm font-semibold text-white">{typeInfo.label}</div>
                                          <div className="text-xs text-[#808080]">
                                            {formatDate(txn.movement_date)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        {txn.to_shares && txn.to_nav && (
                                          <div className="text-sm font-semibold text-white">
                                            {formatNumber(parseFloat(txn.to_shares) * parseFloat(txn.to_nav), 4)} {lot.purchased_asset_denomination}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5 text-xs">
                                      {txn.to_shares && (
                                        <div>
                                          <div className="text-[#808080]">Shares</div>
                                          <div className="text-[#B8B8B8]">{formatNumber(txn.to_shares, 4)}</div>
                                        </div>
                                      )}
                                      {txn.to_nav && (
                                        <div>
                                          <div className="text-[#808080]">NAV</div>
                                          <div className="text-[#B8B8B8]">{formatNumber(txn.to_nav, 4)}</div>
                                        </div>
                                      )}
                                      {txn.denom_asset_price && (
                                        <div>
                                          <div className="text-[#808080]">{lot.purchased_asset_denomination} Price</div>
                                          <div className="text-[#B8B8B8]">{formatCurrency(txn.denom_asset_price, 2)}</div>
                                        </div>
                                      )}
                                      <div>
                                        <div className="text-[#808080]">Value (USD)</div>
                                        <div className="text-[#B8B8B8]">
                                          {formatCurrency(
                                            (parseFloat(txn.to_shares) || 0) * 
                                            (parseFloat(txn.to_nav) || 0) * 
                                            (parseFloat(txn.denom_asset_price) || 0)
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {txn.to_portfolio && (
                                      <div className="text-xs text-[#808080] pt-1">
                                        To: {txn.to_portfolio.name}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-white/5">
        {sortedLots.map((lot) => {
          const gains = parseFloat(lot.dollar_gains) || 0;
          const cagr = parseFloat(lot.cagr) || 0;
          const absReturns = parseFloat(lot.absolute_returns) || 0;
          const isExpanded = expandedLots[lot.lot_id];
          const transactions = lotTransactions[lot.lot_id] || [];
          const isLoadingTxn = loadingTransactions[lot.lot_id];

          return (
            <div key={lot.lot_id} className="p-4 hover:bg-white/[0.02] transition-colors">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleLotExpand(lot.lot_id)}
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-[#676DFF]" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-[#676DFF]" />
                    )}
                  </button>
                  <div>
                    <p className="text-sm font-semibold text-white">{formatDate(lot.date)}</p>
                    <p className="text-xs text-[#808080] mt-0.5">
                      {formatDays(lot.holding_days)} holding
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {cagr >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-400" />
                    )}
                    <span className={`text-lg font-bold ${cagr >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatPercentage(cagr)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-1">
                  <p className="text-xs text-[#808080] uppercase">Purchase</p>
                  <p className="text-sm text-white">
                    {formatNumber(lot.purchased_asset_units, 4)} {lot.purchased_asset_denomination}
                  </p>
                  <p className="text-xs text-[#808080]">@ {formatCurrency(lot.purchase_price, 2)}</p>
                  <p className="text-xs text-[#B8B8B8] font-semibold">{formatCurrency(lot.purchase_value)}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-[#808080] uppercase">Current</p>
                  <p className="text-sm text-white">
                    {formatNumber(lot.current_asset_units, 4)} {lot.purchased_asset_denomination}
                  </p>
                  <p className="text-xs text-[#808080]">@ {formatCurrency(lot.current_asset_price, 2)}</p>
                  <p className="text-xs text-[#B8B8B8] font-semibold">{formatCurrency(lot.current_asset_value)}</p>
                </div>
              </div>

              <div className="flex justify-between items-center pt-3 border-t border-white/5">
                <div>
                  <p className="text-xs text-[#808080]">P&L</p>
                  <p className={`text-sm font-semibold ${gains >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {gains >= 0 ? '+' : ''}{formatCurrency(gains)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#808080]">Returns</p>
                  <p className={`text-sm ${absReturns >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatPercentage(absReturns)}
                  </p>
                </div>
              </div>

              {/* Expanded transaction details for mobile */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4 text-[#676DFF]" />
                    <h4 className="text-sm font-semibold text-white">Transactions</h4>
                  </div>
                  
                  {isLoadingTxn ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="h-4 w-4 text-[#676DFF] animate-spin" />
                    </div>
                  ) : transactions.length === 0 ? (
                    <p className="text-xs text-[#808080] py-2">No transactions found</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.map((txn, idx) => {
                        const typeInfo = getMovementTypeInfo(txn.movement_type);
                        const TypeIcon = typeInfo.icon;
                        
                        return (
                          <div 
                            key={txn.movement_id || idx} 
                            className="p-3 rounded-lg bg-[#151515] border border-white/5 space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <TypeIcon className={`h-4 w-4 ${typeInfo.color}`} />
                                <div>
                                  <div className="text-sm font-semibold text-white">{typeInfo.label}</div>
                                  <div className="text-xs text-[#808080]">
                                    {formatDate(txn.movement_date)}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                {txn.to_shares && txn.to_nav && (
                                  <div className="text-sm font-semibold text-white">
                                    {formatNumber(parseFloat(txn.to_shares) * parseFloat(txn.to_nav), 4)} {lot.purchased_asset_denomination}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5 text-xs">
                              {txn.to_shares && (
                                <div>
                                  <div className="text-[#808080]">Shares</div>
                                  <div className="text-[#B8B8B8] font-medium">{formatNumber(txn.to_shares, 4)}</div>
                                </div>
                              )}
                              {txn.to_nav && (
                                <div>
                                  <div className="text-[#808080]">NAV</div>
                                  <div className="text-[#B8B8B8] font-medium">{formatNumber(txn.to_nav, 4)}</div>
                                </div>
                              )}
                              {txn.denom_asset_price && (
                                <div>
                                  <div className="text-[#808080]">{lot.purchased_asset_denomination} Price</div>
                                  <div className="text-[#B8B8B8] font-medium">{formatCurrency(txn.denom_asset_price, 2)}</div>
                                </div>
                              )}
                              <div>
                                <div className="text-[#808080]">Value (USD)</div>
                                <div className="text-[#B8B8B8] font-medium">
                                  {formatCurrency(
                                    (parseFloat(txn.to_shares) || 0) * 
                                    (parseFloat(txn.to_nav) || 0) * 
                                    (parseFloat(txn.denom_asset_price) || 0)
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {txn.to_portfolio && (
                              <div className="text-xs text-[#808080] pt-1">
                                To: {txn.to_portfolio.name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InvestmentLotsSection;
